import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { CircularProgress } from "@mui/material"
import { endOfDay, format, isSameDay, isValid, subMonths } from "date-fns"

import useRouter from "@/hooks/router/useRoute"
import { useFilteredActivitiesQuery } from "@/hooks/api/useActivityQuery"
import { readActivityDetail } from "@/app/api/activity"
import {
  buildDailyReportUrl,
  buildFilteredActivitiesParams,
  getActivityColor,
  getButtonDisabledByStatus,
  getViewReportButtonText,
} from "@/app/activity/list/utils"
import type { FilteredActivityItem } from "@/app/api/activity"
import { ReportStatus } from "@/types/report"
// [무한스크롤 — 재활용 대비 보존]
// import { useEffect, useRef } from "react"
// import { useActivityOverviewInfiniteQuery } from "@/hooks/api/useActivityOverviewInfiniteQuery"
import { createActivityCommonStore, createActivityStore } from "@/app/activity/store"
import type { Activity } from "@/types/activity"

import ActivityInfoDialog from "@/app/activity/list/activityInfoDialog/ActivityInfoDialog"
import Icon from "@/components/icon/Icon"

import "./activityScheduleView.scss"

interface ScheduleGroup {
  date: Date
  dateKey: string
  activities: Activity[]
}

type DetailState = {
  open: boolean
  anchorEl: HTMLElement | null
  activity: Activity | null
}

const initialDetailState: DetailState = {
  open: false,
  anchorEl: null,
  activity: null,
}

function safeDate(value: string | undefined | null): Date | null {
  if (!value) {
    return null
  }
  const d = new Date(value)

  return Number.isNaN(d.getTime()) ? null : d
}

function formatDuration(activity: Activity): string {
  const start = safeDate(activity.dateStart)
  const finish = safeDate(activity.dateFinish)
  if (!start || !finish) {
    return "--:-- ~ --:--"
  }

  return `${format(start, "HH:mm:ss")} ~ ${format(finish, "HH:mm:ss")}`
}

export default function ActivityScheduleView() {
  const { t } = useTranslation()
  const router = useRouter()

  const { currentDate, filters } = createActivityCommonStore((state) => ({
    currentDate: state.currentDate,
    filters: state.filters,
  }))

  const { setTempActivity, setActivity, setUpsertMode } = createActivityStore((state) => ({
    setTempActivity: state.setTempActivity,
    setActivity: state.setActivity,
    setUpsertMode: state.setUpsertMode,
  }))

  const [detailState, setDetailState] = useState<DetailState>(initialDetailState)
  const closeDetail = () => {
    setDetailState((s) => ({ ...s, open: false }))
  }
  const openDetail = (activity: Activity, anchorEl: HTMLElement) => {
    setDetailState({ open: true, anchorEl, activity })
  }
  const handleEdit = (activity: Activity) => {
    setActivity(activity)
    setTempActivity(activity)
    setUpsertMode(true)
  }

  // 로드 범위: 롤링 1개월 (직전 1개월 ~ currentDate)
  const rangeStart = useMemo(() => subMonths(currentDate, 1).valueOf(), [currentDate])
  const rangeEnd = useMemo(() => endOfDay(currentDate).valueOf(), [currentDate])

  const { data, isLoading } = useFilteredActivitiesQuery(
    buildFilteredActivitiesParams(filters, rangeStart, rangeEnd),
  )

  // [무한스크롤 — 재활용 대비 보존]
  // const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
  //   useActivityOverviewInfiniteQuery({
  //     initialDate: currentDate,
  //     type: filters.types.length ? filters.types.join(",") : undefined,
  //     idfPlayer: filters.playerIds.length ? filters.playerIds.join(",") : undefined,
  //     idfSeasonWeek: filters.seasonWeekIds.length ? filters.seasonWeekIds.join(",") : undefined,
  //     matchDay: filters.matchDays.length ? filters.matchDays.join(",") : undefined,
  //   })

  const groups = useMemo<ScheduleGroup[]>(() => {
    const allActivities: { activity: Activity; date: Date }[] = []

    ;(data ?? []).forEach((item) => {
      const activity = item as unknown as Activity
      const date = safeDate(activity.dateStart)
      if (!date) {
        return
      }
      allActivities.push({ activity, date })
    })

    const cutoff = endOfDay(currentDate).getTime()
    const visible = allActivities.filter(({ date }) => date.getTime() <= cutoff)

    visible.sort((a, b) => {
      const dateDiff = b.date.getTime() - a.date.getTime()
      if (dateDiff !== 0) {
        return dateDiff
      }

      return (a.activity.name ?? "").localeCompare(b.activity.name ?? "")
    })

    const grouped = new Map<string, ScheduleGroup>()
    visible.forEach(({ activity, date }) => {
      const dateKey = format(date, "yyyy-MM-dd")
      const existing = grouped.get(dateKey)
      if (existing) {
        existing.activities.push(activity)
      } else {
        grouped.set(dateKey, { date, dateKey, activities: [activity] })
      }
    })

    const result = Array.from(grouped.values())

    const currentDateKey = format(currentDate, "yyyy-MM-dd")
    if (result[0]?.dateKey !== currentDateKey) {
      result.unshift({ date: currentDate, dateKey: currentDateKey, activities: [] })
    }

    return result
  }, [data, currentDate])

  // [무한스크롤 — next 방향만 자동 fetch — 재활용 대비 보존]
  // const bottomSentinelRef = useRef<HTMLDivElement | null>(null)
  // const scrollRef = useRef<HTMLDivElement | null>(null)
  // const stateRef = useRef({ hasNextPage, isFetchingNextPage })
  // stateRef.current = { hasNextPage, isFetchingNextPage }
  //
  // useEffect(() => {
  //   const root = scrollRef.current
  //   const bottomEl = bottomSentinelRef.current
  //   if (!root || !bottomEl) {
  //     return
  //   }
  //
  //   const observer = new IntersectionObserver(
  //     (entries) => {
  //       const entry = entries[0]
  //       if (!entry?.isIntersecting) {
  //         return
  //       }
  //       const { hasNextPage: hasNext, isFetchingNextPage: fetching } = stateRef.current
  //       if (hasNext && !fetching) {
  //         fetchNextPage()
  //       }
  //     },
  //     { root, rootMargin: "0px 0px 80px 0px", threshold: 0 },
  //   )
  //
  //   observer.observe(bottomEl)
  //
  //   return () => observer.disconnect()
  // }, [fetchNextPage])

  const handleActivityDetails = (activity: Activity) => {
    if (!activity.idfActivity) {
      return
    }
    router.push(`/activity/detail?activity=${activity.idfActivity}`)
  }

  const handleViewReport = async (activity: Activity) => {
    if (!activity.idfActivity) {
      return
    }
    // 목록 응답에는 세션/선수 리스트가 없어 상세 조회로 확보 (실패 시 리포트 페이지의 fallback 에 위임)
    const detail = await readActivityDetail(activity.idfActivity)
      .then((res) => res.response)
      .catch(() => null)
    router.push(buildDailyReportUrl(activity.idfActivity, detail))
  }

  const showInitialLoader = isLoading && groups.length === 0

  return (
    <>
      <div className="ActivityScheduleView">
        <div className="ActivityScheduleView__header">
          <div className="ActivityScheduleView__header-date">{t("date", "Date")}</div>
          <div className="ActivityScheduleView__header-list">
            <span>{t("activityName", "Activity Name")}</span>
            <span>{t("duration", "Duration")}</span>
            <span>{t("session", "Session")}</span>
            <span>{t("player", "Player")}</span>
            <span className="ActivityScheduleView__col-actions" />
          </div>
        </div>

        <div className="ActivityScheduleView__scroll">
          {showInitialLoader && (
            <div className="ActivityScheduleView__loader">
              <CircularProgress size={24} />
            </div>
          )}

          {groups.map((group) => {
            const { date, dateKey, activities } = group
            const dayLabel = isValid(date) ? format(date, "d") : "--"
            const suffix = isValid(date)
              ? `${format(date, "MMM")}, ${format(date, "EEE").toUpperCase()}`
              : ""
            const isTodayDate = isValid(date) && isSameDay(date, currentDate)

            return (
              <div key={dateKey} className="ActivityScheduleView__group">
                <div className="ActivityScheduleView__date-cell">
                  <span
                    className={`ActivityScheduleView__day${isTodayDate ? " ActivityScheduleView__day--today" : ""}`}>
                    {dayLabel}
                  </span>
                  <span className="ActivityScheduleView__suffix">{suffix}</span>
                </div>

                <div className="ActivityScheduleView__list">
                  {activities.length === 0 && (
                    <div className="ActivityScheduleView__item ActivityScheduleView__item--empty" />
                  )}
                  {activities.map((activity) => {
                    // /activities/filtered 응답(FilteredActivityItem)에는 분석 status 가 포함됨
                    const reportStatus = (activity as unknown as FilteredActivityItem)
                      .status as ReportStatus

                    return (
                      <div
                        key={activity.idfActivity ?? activity.name}
                        className="ActivityScheduleView__item"
                        onClick={(e) => openDetail(activity, e.currentTarget)}>
                        <div className="ActivityScheduleView__name-cell">
                          <span
                            className="ActivityScheduleView__dot"
                            style={{ background: getActivityColor(activity.type) }}
                          />
                          <span className="ActivityScheduleView__name">{activity.name}</span>
                          {activity.isLive === true && (
                            <span className="ActivityScheduleView__live">{t("live", "Live")}</span>
                          )}
                          <span className="ActivityScheduleView__chevron">
                            <Icon type="arrow-right" />
                          </span>
                        </div>

                        <span className="ActivityScheduleView__duration">
                          {formatDuration(activity)}
                        </span>
                        <span className="ActivityScheduleView__count">
                          {activity.sessionCount ?? 0}
                        </span>
                        <span className="ActivityScheduleView__count">
                          {activity.playerCount ?? 0}
                        </span>

                        <div className="ActivityScheduleView__actions">
                          <button
                            type="button"
                            className="ActivityScheduleView__action-btn"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleActivityDetails(activity)
                            }}>
                            {t("activityDetails", "Activity Details")}
                          </button>
                          <button
                            type="button"
                            className="ActivityScheduleView__action-btn ActivityScheduleView__action-btn--primary"
                            disabled={getButtonDisabledByStatus(reportStatus)}
                            onClick={(e) => {
                              e.stopPropagation()
                              handleViewReport(activity)
                            }}>
                            {getViewReportButtonText(reportStatus, t)}
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}

          {/* [무한스크롤 — 재활용 대비 보존]
          {isFetchingNextPage && (
            <div className="ActivityScheduleView__loader">
              <CircularProgress size={18} />
            </div>
          )}
          <div className="ActivityScheduleView__sentinel" ref={bottomSentinelRef} /> */}
        </div>
      </div>
      {detailState.activity && (
        <ActivityInfoDialog
          open={detailState.open}
          anchorEl={detailState.anchorEl}
          activity={detailState.activity}
          onClose={closeDetail}
          onEdit={handleEdit}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        />
      )}
    </>
  )
}
